import React from "react"
import {
  useReactTable,
  getCoreRowModel,
  getExpandedRowModel,
  flexRender,
} from "@tanstack/react-table"
import { Table, Button, Badge } from "reactstrap"
import { AdvancedTablePagination, TablePagination } from "components/Common/TableContainer"

const CollapsibleDataTable = ({
  columns,
  data,
  renderSubComponent,
  childKey = "children",
  isBordered = false,
  isPagination = false,
  paginationInfo = null,
  onPageChange = null,
  loading = false,
  divClassName = "table-responsive",
  theadClass,
  tableClass = "align-middle table-nowrap dt-responsive nowrap w-100 table-check dataTable no-footer dtr-inline mt-4 border-top",
  customCountField = null,
  paginationType = "advanced", // 'standard' or 'advanced'
}) => {
  const [expanded, setExpanded] = React.useState({})

  const table = useReactTable({
    data,
    columns,
    state: { expanded },
    onExpandedChange: setExpanded,
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getRowCanExpand: row =>
      Array.isArray(row.original?.[childKey]) &&
      row.original?.[childKey].length > 0,
  })

  const renderChildCount = row => {
    let count = null

    if (typeof customCountField === "function") {
      count = customCountField(row)
    } else if (
      Array.isArray(row.original?.[childKey]) &&
      row.original?.[childKey].length > 0
    ) {
      count = row.original?.[childKey].length
    }

    if (count && count > 0)
      return (
        <Badge color="danger" pill className="ms-1" style={{ fontSize: 11 }}>
          {count - 1}
        </Badge>
      )

    return null
  }

  return !loading ? (
    <>
      <div className={divClassName}>
        <Table hover bordered={isBordered} responsive className={tableClass}>
          <thead className={theadClass}>
            {table.getHeaderGroups().map(headerGroup => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map(header => (
                  <th key={header.id} className="text-nowrap">
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>

          <tbody>
            {table.getRowModel().rows.length > 0 ? (
              table.getRowModel().rows.map(row => (
                <React.Fragment key={row.id}>
                  <tr>
                    {row.getVisibleCells().map(cell => (
                      <td key={cell.id} className="align-middle">
                        {cell.column.id === "expander" ? (
                          row.getCanExpand() ? (
                            <Button
                              color="light"
                              size="sm"
                              className="p-0 text-dark d-flex align-items-center"
                              onClick={row.getToggleExpandedHandler()}
                              title={
                                row.getIsExpanded() ? "Collapse" : "Expand"
                              }
                            >
                              <i
                                className={`bx ${
                                  row.getIsExpanded()
                                    ? "bx-chevron-down"
                                    : "bx-chevron-right"
                                } fs-5 align-middle`}
                              ></i>
                              {renderChildCount(row)}
                            </Button>
                          ) : (
                            <span className="text-muted small">—</span>
                          )
                        ) : (
                          flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )
                        )}
                      </td>
                    ))}
                  </tr>

                  {row.getIsExpanded() && (
                    <tr>
                      <td
                        colSpan={row.getVisibleCells()?.length}
                        className="bg-light"
                      >
                        {renderSubComponent({ row })}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))
            ) : (
              <tr>
                <td colSpan={columns.length} className="text-center">
                  No data available
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>

      {isPagination && paginationInfo && paginationInfo.total > 0 && (
        <>
          {paginationType === "advanced" ? (
            <AdvancedTablePagination
              paginationInfo={paginationInfo}
              onPageChange={onPageChange}
            />
          ) : (
            <TablePagination
              paginationInfo={paginationInfo}
              onPageChange={onPageChange}
            />
          )}
        </>
      )}
    </>
  ) : (
    <div className="mt-4">
      <span
        className="placeholder w-100"
        style={{ height: 45, backgroundColor: "#eff2f7" }}
      ></span>
      {Array.from({ length: 10 }).map((_, index) => (
        <span
          className="placeholder w-100 mt-2"
          style={{ height: 35, backgroundColor: "#eff2f7" }}
          key={index}
        ></span>
      ))}
    </div>
  )
}

export default CollapsibleDataTable
